import { createAction, createSlice } from '@reduxjs/toolkit'
import { Location } from '../../@types/location'
import { Organization } from '../../@types/organization'
import { Template } from '../../@types/template'
import { TemplateQuestion } from '../../@types/templateQuestion'
import { User } from '../../@types/user'
import { UserRole } from '../../@types/userRole'
import { Document } from './../../@types/document'
import { createDocumentFromTemplate } from './actions/createDocumentFromTemplate'
import { findLocations } from './actions/findLocations'
import { findTemplateQuestions } from './actions/findTemplateQuestions'
import { findUserRoles } from './actions/findUserRoles'
import { findTemplatesMassiveDocumments } from './actions/findTemplates'
import { findLegalReps } from './actions/findLegalRep'
import { SelectedUserInfo } from '../../@types/SelectedUserInfo'
import { findSelectedUsers } from './actions/findSelectedUsers'
import { findSingleUserRole } from './actions/findSingleUserRole'
import { findSingleUserRoleByCode } from './actions/findSingleUserRoleByCode'

interface MassiveDocumentCreate {
  document: Document
  templateQuestions: TemplateQuestion[]
  createStep: number
  selectedTemplate?: Template
  selectedUsers: UserRole[]
  templateList: Template[]
  searchText: string
  legalRep: User
  legalRepList: User[]
  createFilters: {
    location: Location
    organization: Organization
    range: string
  }
  selectedUsersInfo: SelectedUserInfo[]
  selectedUsersInfoStatus: string
  creationType: string

  userRole: UserRole[]
  locations: Location[]
  findUserRoleStatus: string
  findSingleUserRoleByCodeStatus: 'idle' | 'pending' | 'succeeded' | 'failed'
  createStatus: string
  createdCounter: number
  processId: number
  createProcessStatus: string
  metadata: []
  richText: any
}

const initialState = {
  templateQuestions: [],
  createMethod: '',
  createStep: 1,
  selectedTemplate: {} as Template,
  selectedUsers: [],
  document: {} as Document,
  templateList: [] as Template[],
  legalRepList: [] as User[],
  searchText: '',
  legalRep: {} as User,
  createFilters: {
    location: {} as Location,
    organization: {} as Organization,
    range: ''
  },
  userRole: [],
  locations: [],
  findUserRoleStatus: '',
  findSingleUserRoleByCodeStatus: 'idle',
  createStatus: '',
  createdCounter: 0,
  selectedUsersInfo: [],
  selectedUsersInfoStatus: '',
  creationType: 'Plantilla',
  processId: 0,
  createProcessStatus: '',
  metadata: [],
  richText: ''
} as MassiveDocumentCreate

export const setQuestionAnswer = createAction<TemplateQuestion>('MASSIVE_CREATE_SET_QUESTION_ANSWER')
export const setQuestionUser = createAction<TemplateQuestion>('MASSIVE_CREATE_SET_QUESTION_USER')
export const setSelectedTemplate = createAction<Template>('MASSIVE_CREATE_SET_SELECTED_TEMPLATE')

export const setCreateStep = createAction<number>('MASSIVE_CREATE_SET_STEP_NUMBER')
export const setDocumentCreateData = createAction<any>('MASSIVE_CREATE_SET_DOCUMENT_FIRST_DATA')
export const setNextDocumentCreateData = createAction<any>('MASSIVE_CREATE_SET_NEXT_DOCUMENT_FIRST_DATA')
export const setTemplateRichtext = createAction<string>('MASSIVE_CREATE_SET_TEMPLATE_RICHTEXT_CREATE')
export const setSearchText = createAction<string>('MASSIVE_CREATE_SET_DOCUMENT_CREATE_SEARCHTEXT')
export const setUsers = createAction<User[]>('MASSIVE_CREATE_SET_DOCUMENT_CREATE_USERS')
export const setLegalRep = createAction<User>('MASSIVE_CREATE_SET_DOCUMENT_CREATE_LEGAL_REP')
export const setLocation = createAction<Location>('MASSIVE_CREATE_SET_DOCUMENT_CREATE_LOCATION')
export const setOrganization = createAction<Organization>('MASSIVE_CREATE_SET_DOCUMENT_CREATE_ORGANIZATION')
export const setRange = createAction<string>('MASSIVE_CREATE_SET_DOCUMENT_CREATE_RANGE')

export const clearCreateFilters = createAction('MASSIVE_CREATE_CLEAR_CREATE_FILTERS')
export const setPreselectUser = createAction<UserRole>('MASSIVE_CREATE_SET_PRESELECT_USER')
export const setDeselectUser = createAction<UserRole>('MASSIVE_CREATE_SET_DESELECT_USER')
export const setPreselectAll = createAction<UserRole[]>('MASSIVE_CREATE_SET_PRESELECT_ALL_USER')
export const setDeselectAll = createAction('MASSIVE_CREATE_SET_DESELECT_ALL_USER')
export const setCreateStatus = createAction<string>('setCreateStatus')
export const setProcessId = createAction<any>('setProcessId')
export const setMetaData = createAction<any>('setMetaData')
export const setRichText = createAction<any>('setRichText')

const massiveDocumentCreateSlice = createSlice({
  name: 'massiveDocumentCreate',
  initialState,
  reducers: {
    setInitialData() {
      return initialState
    },
    incrementCreatedCounter(state) {
      state.createdCounter += 1;
    },
    resetCreatedCounter: (state) => {
      state.createdCounter = 0;
    },
    resetUserRoles: (state) => {
      state.userRole = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(
        setDocumentCreateData,
        (
          state,
          {
            payload: {
              title,
              category,
              template,
              legalRep,
              signatureProvider,
              creationType, // ✅ incluir aquí
              archive,               // ✅ agregar
              archiveFilename        // ✅ agregar
            }
          }
        ) => {
          state.document.title = title;
          state.document.category = category;
          state.selectedTemplate = template;
          state.legalRep = legalRep;
          state.createdCounter = 0;
          state.createStatus = "";
          state.document.signatureProvider = signatureProvider;
          state.creationType = creationType ?? "Plantilla";
          state.document.archive = archive;                 // ✅ guardar
          state.document.archiveFilename = archiveFilename; // ✅ guardar
        }
      )
      .addCase(
        setNextDocumentCreateData,
        (
          state,
          {
            payload: {
              title,
              category,
              template,
              legalRep,
              signatureProvider,
              creationType // ✅ también aquí
            }
          }
        ) => {
          state.document.title = title
          state.document.category = category
          state.selectedTemplate = template
          state.legalRep = legalRep
          state.document.signatureProvider = signatureProvider
          state.creationType = creationType ?? state.creationType // ✅ mantener si no se envía
        }
      )
      .addCase(setQuestionAnswer, (state, { payload: { order: questionOrder, value } }) => {
        let question = state.templateQuestions.find(({ order }) => questionOrder === order)
        if (question) {
          question.value = value
        }
      })
      .addCase(setQuestionUser, (state, { payload: { order: questionOrder, user } }) => {
        let question = state.templateQuestions.find(({ order }) => questionOrder === order)
        if (question) {
          question.user = user
        }
      })
      .addCase(setSelectedTemplate, (state, { payload }) => {
        state.selectedTemplate = payload
      })
      .addCase(setCreateStep, (state, { payload }) => {
        state.createStep = payload
      })
      .addCase(setSearchText, (state, { payload }) => {
        state.searchText = payload
      })
      .addCase(setLocation, (state, { payload }) => {
        state.createFilters.location = payload
      })
      .addCase(setOrganization, (state, { payload }) => {
        state.createFilters.organization = payload
      })
      .addCase(setRange, (state, { payload }) => {
        state.createFilters.range = payload
      })

      .addCase(clearCreateFilters, (state) => {
        state.createFilters = {
          location: {} as Location,
          organization: {} as Organization,
          range: ''
        }
      })
      .addCase(findUserRoles.pending, (state) => {
        state.findUserRoleStatus = 'pending'
      })
      .addCase(findUserRoles.fulfilled, (state, { payload }) => {
        state.findUserRoleStatus = 'fulfilled'
        state.userRole = payload
      })
      .addCase(findSingleUserRole.pending, (state) => {
        state.findUserRoleStatus = 'pending'
      })
      .addCase(findSingleUserRole.fulfilled, (state, { payload }) => {
        state.findUserRoleStatus = 'fulfilled'
        state.userRole = payload
      })
      .addCase(findLocations.fulfilled, (state, { payload }) => {
        state.locations = payload
      })
      .addCase(findTemplateQuestions.fulfilled, (state, { payload }) => {
        state.templateQuestions = payload
      })
      .addCase(setPreselectUser, (state, { payload }) => {
        state.selectedUsers = [...state.selectedUsers, payload]
      })
      .addCase(setDeselectUser, (state, { payload }) => {
        state.selectedUsers = state.selectedUsers.filter(({ id }) => id !== payload.id)
      })
      .addCase(setPreselectAll, (state, { payload }) => {
        state.selectedUsers = [...state.selectedUsers, ...payload]
      })
      .addCase(setDeselectAll, (state) => {
        state.selectedUsers = []
      })
      .addCase(setCreateStatus, (state, { payload }) => {
        state.createStatus = payload
      })
      .addCase(createDocumentFromTemplate.fulfilled, (state) => {
        state.createdCounter = state.createdCounter + 1
      })
      .addCase(findTemplatesMassiveDocumments.fulfilled, (state, { payload }) => {
        state.templateList = payload
      })
      .addCase(findLegalReps.fulfilled, (state, { payload }) => {
        state.legalRepList = payload
      })
      .addCase(findSelectedUsers.pending, (state) => {
        state.selectedUsersInfoStatus = 'pending'
      })
      .addCase(findSelectedUsers.fulfilled, (state, { payload }) => {
        state.selectedUsersInfo = payload
        state.selectedUsersInfoStatus = 'fulfilled'
      })
      .addCase(setProcessId, (state, { payload }) => {
        state.processId = payload
      })
      .addCase(setRichText, (state, { payload }) => {
        state.richText = payload
      })
      .addCase(setMetaData, (state, { payload }) => {
        state.metadata = payload
      })
      .addCase(findSingleUserRoleByCode.pending, (state) => {
        state.userRole = []
        state.findSingleUserRoleByCodeStatus = 'pending'
      })
      .addCase(findSingleUserRoleByCode.rejected, (state) => {
        state.userRole = []
        state.findSingleUserRoleByCodeStatus = 'failed'
      })
      .addCase(findSingleUserRoleByCode.fulfilled, (state, { payload }) => {
        state.userRole = payload
        state.findSingleUserRoleByCodeStatus = 'succeeded'
      })
  }
})

export const { setInitialData, incrementCreatedCounter, resetCreatedCounter, resetUserRoles } = massiveDocumentCreateSlice.actions;

export const massiveDocumentCreate = massiveDocumentCreateSlice.reducer
